import { create, StateCreator } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AdminUser } from '@/types';

interface AdminState {
  token: string | null;
  admin: AdminUser | null;
  setAuth: (token: string, admin: AdminUser) => void;
  logout: () => void;
  hasPermission: (code: string) => boolean;
  hasAnyPermission: (codes: string[]) => boolean;
  isSuperAdmin: () => boolean;
}

const createAdminState: StateCreator<AdminState> = (set, get) => ({
  token: null,
  admin: null,
  setAuth: (token, admin) => set({ token, admin }),
  logout: () => {
    set({ token: null, admin: null });
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  },
  hasPermission: (code: string): boolean => {
    const { admin } = get();
    if (admin?.role?.code === 'super_admin') return true;
    return admin?.permissions?.includes(code) ?? false;
  },
  hasAnyPermission: (codes: string[]): boolean => {
    const { admin } = get();
    if (admin?.role?.code === 'super_admin') return true;
    return codes.some(code => admin?.permissions?.includes(code)) ?? false;
  },
  isSuperAdmin: (): boolean => {
    const { admin } = get();
    return admin?.role?.code === 'super_admin';
  },
});

export const useAdminStore = create<AdminState>()(
  persist(createAdminState, {
    name: 'pmsupers-admin-auth',
    storage: {
      getItem: (name) => {
        const value = localStorage.getItem(name);
        return value ? JSON.parse(value) : null;
      },
      setItem: (name, value) => {
        localStorage.setItem(name, JSON.stringify(value));
        if (value.state?.token) {
          localStorage.setItem('admin_token', value.state.token);
        }
        if (value.state?.admin) {
          localStorage.setItem('admin_user', JSON.stringify(value.state.admin));
        }
      },
      removeItem: (name) => {
        localStorage.removeItem(name);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
      },
    },
  })
);
